import type { MouseEventHandler, ReactElement, ReactNode } from 'react';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { OutsideClickHandler } from '@/ui/outside-click-handler/outside-click-handler';
import { usePopper } from 'react-popper';
import { styled } from 'goober';

import { classList } from '@/helpers/class-list';

import type { IconVariant } from '../icons/icon';
import { Portal } from '../portal/portal';

import { DropdownList } from './dropdown.styles';
import { DropdownItemSubList } from './dropdown-item-sub-list';
import { DropdownListItem } from './dropdown-list-item';
import { Tooltip } from '../tooltip/tooltip';

interface DropdownBaseOption {
  label: ReactElement | string;
  subtext?: string;
  onClick?: () => void;
  isDisabled?: boolean;
  isVisible?: boolean;
  icon?: IconVariant;
  tooltipContent?: ReactNode;
  isTooltipVisible?: boolean;
}

export interface DropdownOption extends DropdownBaseOption {
  options?: DropdownBaseOption[];
}

interface WithDropdownProps {
  disabled?: boolean;
  testId?: string;
  items: DropdownOption[];
  children: React.ReactNode;
  options?: Parameters<typeof usePopper>[2];
  fullWidth?: boolean;
  hideOnNoItems?: boolean;
  setIsDropdownOpened?: (isOpened: boolean) => void;
  maxWidth?: string;
}
const DEFAULT_MAX_WIDTH = '210px';

export function WithDropdown({
  disabled,
  testId,
  children,
  items,
  options,
  fullWidth = false,
  hideOnNoItems = false,
  setIsDropdownOpened,
  maxWidth = DEFAULT_MAX_WIDTH,
}: WithDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(
    null,
  );
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const popperOptions = useMemo(
    () => ({
      placement: options?.placement ?? 'bottom-start',
      strategy: options?.strategy ?? 'fixed',
      modifiers: [
        { name: 'offset', options: { offset: [0, 6] } },
        {
          name: 'preventOverflow',
          options: { boundary: 'viewport', padding: 8 },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: [
              'top-start',
              'bottom-end',
              'bottom-start',
              'top-end',
            ],
          },
        },
        ...(options?.modifiers ?? []),
      ],
    }),
    [options],
  );

  const { styles, attributes, update } = usePopper(
    referenceElement,
    popperElement,
    popperOptions,
  );

  useEffect(() => {
    setIsDropdownOpened?.(isOpen);
  }, [isOpen, setIsDropdownOpened]);

  useEffect(() => {
    if (!isOpen) return;
    void update?.();
  }, [isOpen, update, referenceElement, popperElement]);

  useEffect(() => {
    if (!isOpen) return;
    const handleReposition = () => {
      void update?.();
    };
    window.addEventListener('resize', handleReposition);
    document.addEventListener('visibilitychange', handleReposition);
    return () => {
      window.removeEventListener('resize', handleReposition);
      document.removeEventListener('visibilitychange', handleReposition);
    };
  }, [isOpen, update]);

  const toggleDropdown: MouseEventHandler<HTMLDivElement> = event => {
    event.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleOnMenuItemClick = (callback?: () => void) => {
    callback?.();
    setIsOpen(false);
  };

  const handleClickOutside = () => {
    setIsOpen(false);
  };
  const visibleItems = items?.filter(item => item.isVisible !== false);

  if (!visibleItems?.length && hideOnNoItems === false) {
    return null;
  }

  if (disabled) return <>{children}</>;

  return (
    <OutsideClickHandler onOutsideClick={handleClickOutside}>
      <MenuButtonContainer
        ref={setReferenceElement}
        onClick={toggleDropdown}
        className={classList(isOpen ? 'is-open' : 'is-closed')}
      >
        {children}
      </MenuButtonContainer>
      <Portal>
        <DropdownList
          data-testid={testId}
          className={classList(!isOpen && 'hidden')}
          ref={setPopperElement}
          style={{
            ...styles.popper,
            ...(fullWidth
              ? { width: `${referenceElement?.offsetWidth}px` }
              : { maxWidth }),
          }}
          {...attributes.popper}
        >
          {visibleItems?.map((item, index) => {
            const subMenu =
              item.options?.filter(subItem => subItem.isVisible !== false) ??
              [];
            return (
              <Tooltip
                key={`${index}`}
                content={item.tooltipContent}
                visible={!!item.isTooltipVisible}
              >
                <DropdownListItem
                  item={item}
                  onClick={event => {
                    event.stopPropagation();
                    handleOnMenuItemClick(item.onClick);
                  }}
                >
                  <DropdownItemSubList
                    items={subMenu}
                    onClick={handleOnMenuItemClick}
                  />
                </DropdownListItem>
              </Tooltip>
            );
          })}
        </DropdownList>
      </Portal>
    </OutsideClickHandler>
  );
}

const MenuButtonContainer = styled('div', forwardRef)`
  &:hover {
    cursor: pointer;
  }
`;
